import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { FaUserCircle } from "react-icons/fa";
import authService from '../services/authService';
import { useUser } from '../context/UserContext';

export default function Navbar({ isAuthenticated, role }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [isNavCollapsed, setIsNavCollapsed] = useState(true);
  const { userName, userEmail } = useUser();

  // Create userData object from context for compatibility
  const userData = {
    username: userName,
    email: userEmail,
    role: role
  };

  // Function to determine if a link is active
  const isActive = (path) => {
    return location.pathname === path;
  };

  // Function to determine if a dropdown item is active
  const isDropdownActive = (paths) => {
    return Array.isArray(paths) ? paths.some(path => location.pathname === path) : location.pathname === paths;
  };

  const handleLogout = async () => {
    try {
      await authService.logout();
      navigate('/login');
    } catch (e) {
      navigate('/login');
    }
  };

  const handleNavToggle = () => {
    setIsNavCollapsed(!isNavCollapsed);
  };

  return (
    <nav className="navbar navbar-expand-lg navbar-light bg-light mb-3">
      <div className="container-fluid">
        <Link className="navbar-brand" to={isAuthenticated ? (role === 'admin' ? '/admin' : '/dashboard') : '/login'}>
          🎓 AI Klassroom
        </Link>
        <button
          className="navbar-toggler"
          type="button"
          onClick={handleNavToggle}
          aria-controls="navbarSupportedContent"
          aria-expanded={!isNavCollapsed}
          aria-label="Toggle navigation"
        >
          <span className="navbar-toggler-icon"></span>
        </button>
        <div className={`collapse navbar-collapse ${!isNavCollapsed ? 'show' : ''}`} id="navbarSupportedContent">
          <ul className="navbar-nav me-auto mb-2 mb-lg-0">
            {isAuthenticated && role === 'admin' && (
              <>
                <li className="nav-item">
                  <Link className={`nav-link ${isActive('/admin') ? 'active' : ''}`} to="/admin" onClick={() => setIsNavCollapsed(true)}>Admin Dashboard</Link>
                </li>
                <li className="nav-item">
                  <Link className={`nav-link ${isActive('/admin/access-code') ? 'active' : ''}`} to="/admin/access-code" onClick={() => setIsNavCollapsed(true)}>Access Codes</Link>
                </li>
                <li className="nav-item">
                  <Link className={`nav-link ${isActive('/user-management') ? 'active' : ''}`} to="/user-management" onClick={() => setIsNavCollapsed(true)}>
                    <i className="fas fa-user-cog me-2"></i>
                    User Management
                  </Link>
                </li>
                <li className="nav-item">
                  <Link className={`nav-link ${isActive('/activity-prompt-manager') ? 'active' : ''}`} to="/activity-prompt-manager" onClick={() => setIsNavCollapsed(true)}>
                    <i className="fas fa-cog me-2"></i>
                    Prompt Manager
                  </Link>
                </li>
                <li className="nav-item">
                  <Link className={`nav-link ${isActive('/school-management') ? 'active' : ''}`} to="/school-management" onClick={() => setIsNavCollapsed(true)}>
                    <i className="fas fa-school me-2"></i>
                    Create New School
                  </Link>
                </li>
              </>
            )}

            {isAuthenticated && role !== 'admin' && (
              <>
                <li className="nav-item">
                  <Link className={`nav-link ${isActive('/dashboard') ? 'active' : ''}`} to="/dashboard" onClick={() => setIsNavCollapsed(true)}>Home</Link>
                </li>
                <li className="nav-item">
                  <Link className={`nav-link ${isActive('/content') ? 'active' : ''}`} to="/content" onClick={() => setIsNavCollapsed(true)}>Audio DashBoard</Link>
                </li>
                <li className="nav-item">
                  <Link 
                    className={`nav-link ${isDropdownActive(['/maker', '/acttable', '/activity-table', '/school-test']) ? 'active' : ''}`} 
                    to="/maker"
                    onClick={() => setIsNavCollapsed(true)}
                  >
                    School Section
                  </Link>
                </li>
                <li className="nav-item">
                  <Link 
                    className={`nav-link ${isDropdownActive(['/mobile-tables', '/mobile-activity-tables', '/mobile-activity-table-update', '/mobile-test', '/activity-sync-status']) ? 'active' : ''}`} 
                    to="/mobile-tables"
                    onClick={() => setIsNavCollapsed(true)}
                  >
                    Mobile Section
                  </Link>
                </li>
                <li className="nav-item">
                  <Link
                    className={`nav-link ${isActive('/activities') ? 'active' : ''}`}
                    to="/activities"
                    onClick={() => setIsNavCollapsed(true)}
                  >
                    Activities
                  </Link>
                </li>
                <li className="nav-item">
                  <Link
                    className={`nav-link ${isActive('/projector-activities') ? 'active' : ''}`}
                    to="/projector-activities"
                    onClick={() => setIsNavCollapsed(true)}
                  >
                    Projector Activities
                  </Link>
                </li>
                <li className="nav-item">
                  <Link
                    className={`nav-link ${isActive('/offline-activity') ? 'active' : ''}`}
                    to="/offline-activity"
                    onClick={() => setIsNavCollapsed(true)}
                  >
                    Offline Activity
                  </Link>
                </li>
                <li className="nav-item">
                  <Link
                    className={`nav-link ${isActive('/teacher-activity-assignment') ? 'active' : ''}`}
                    to="/teacher-activity-assignment"
                    onClick={() => setIsNavCollapsed(true)}
                  >
                    Activity Assignment
                  </Link>
                </li>

                <li className="nav-item dropdown">
                  <a
                    className={`nav-link dropdown-toggle ${isDropdownActive(['/student-chance-management', '/slide-track-testing-school', '/slide-track-testing-mobile', '/image-tester']) ? 'active' : ''}`}
                    href="#"
                    id="testerDropdown"
                    role="button"
                    data-bs-toggle="dropdown"
                    aria-expanded="false"
                  >
                    <i className="fas fa-vial me-2"></i>
                    Tester
                  </a>
                  <ul className="dropdown-menu" aria-labelledby="testerDropdown">
                    <li>
                      <Link className={`dropdown-item ${isActive('/student-chance-management') ? 'active' : ''}`} to="/student-chance-management" onClick={() => setIsNavCollapsed(true)}>
                        <i className="fas fa-user-graduate me-2"></i>
                        Student Chance Management
                      </Link>
                    </li>
                    <li className="dropdown-submenu">
                      <span className="dropdown-item d-flex justify-content-between align-items-center" style={{ cursor: 'pointer' }}>
                        <span>
                          <i className="fas fa-chart-line me-2"></i>
                          Slide Track Testing
                        </span>
                        <i className="fas fa-chevron-right" style={{ fontSize: '0.8em' }}></i>
                      </span>
                      <ul className="dropdown-menu dropdown-submenu-content">
                        <li>
                          <Link className={`dropdown-item ${isActive('/slide-track-testing-school') ? 'active' : ''}`} to="/slide-track-testing-school" onClick={() => setIsNavCollapsed(true)}>
                            <i className="fas fa-school me-2"></i>
                            School
                          </Link>
                        </li>
                        <li>
                          <Link className={`dropdown-item ${isActive('/slide-track-testing-mobile') ? 'active' : ''}`} to="/slide-track-testing-mobile" onClick={() => setIsNavCollapsed(true)}>
                            <i className="fas fa-mobile-alt me-2"></i>
                            Mobile
                          </Link>
                        </li>
                      </ul>
                    </li>
                    <li>
                      <Link className={`dropdown-item ${isActive('/image-tester') ? 'active' : ''}`} to="/image-tester" onClick={() => setIsNavCollapsed(true)}>
                        <i className="fas fa-images me-2"></i>
                        Image Tester
                      </Link>
                    </li>
                  </ul>
                </li>
              </>
            )}
          </ul>

          <ul className="navbar-nav ms-auto mb-2 mb-lg-0">
            {!isAuthenticated ? (
              <li className="nav-item">
                <Link className="btn btn-outline-primary" to="/login" onClick={() => setIsNavCollapsed(true)}>Login</Link>
              </li>
            ) : (
              <li className="nav-item dropdown">
                <a
                  className="nav-link dropdown-toggle d-flex align-items-center"
                  href="#"
                  id="userProfileDropdown"
                  role="button"
                  data-bs-toggle="dropdown"
                  aria-expanded="false"
                  style={{ cursor: 'pointer' }}
                >
                  <FaUserCircle className="text-primary me-2" size={35} />
                  <span className="d-none d-md-inline">{userData?.username || 'User'}</span>
                </a>
                <ul className="dropdown-menu dropdown-menu-end" aria-labelledby="userProfileDropdown">
                  <li className="px-3 py-2 border-bottom">
                    <div className="d-flex align-items-center">
                      <FaUserCircle className="text-primary me-3" size={45} />
                      <div>
                        <div className="fw-bold">{userData?.username || 'User'}</div>
                        <div className="text-muted small">{userData?.email || ''}</div>
                      </div>
                    </div>
                  </li>
                  <li className="px-3 py-2">
                    <div className="d-flex align-items-center">
                      <i className="fas fa-user-tag me-2 text-muted"></i>
                      <span className="me-2">Role:</span>
                      <span className={`badge ${role === 'admin' ? 'bg-danger' : 'bg-success'}`}>
                        {role === 'admin' ? 'Admin' : role === 'content_creator' ? 'Creator' : 'User'}
                      </span>
                    </div>
                  </li>
                  <li><hr className="dropdown-divider" /></li>
                  <li>
                    <button
                      className="dropdown-item text-danger d-flex align-items-center"
                      onClick={handleLogout}
                    >
                      <i className="fas fa-sign-out-alt me-2"></i>
                      Logout
                    </button>
                  </li>
                </ul>
              </li>
            )}
          </ul>
        </div>
      </div>
    </nav>
  );
}